import React from 'react';
import Select, {
  Props as SelectProps,
  GroupBase,
  CSSObjectWithLabel,
} from 'react-select';

// Standard width for filter operator selects (e.g., "include any of", "is", etc.)
export const FilterOperatorSelectWidth = '160px';
export const FilterSelectHeight = '40px';

// Filter select variants for consistent styling
type FilterSelectVariant = 'operator' | 'value';

// Default styles applied to all select components
const getDefaultStyles = () => ({
  menuPortal: (base: CSSObjectWithLabel) => ({
    ...base,
    left: undefined, // Resets any custom left positioning
    top: undefined, // Resets any custom top positioning
  }),
  control: (base: CSSObjectWithLabel) => ({
    ...base,
    minHeight: FilterSelectHeight, // Consistent height for better alignment with other UI elements
  }),
});

// Variant-specific styles
const getVariantStyles = () => ({
  // Operator variant (fixed width for operator selects)
  operator: {
    container: (base: CSSObjectWithLabel) => ({
      ...base,
      minWidth: FilterOperatorSelectWidth,
      flex: 'none', // Don't grow
    }),
  },
  // Value variant (flexible width for value selects)
  value: {
    container: (base: CSSObjectWithLabel) => ({
      ...base,
      flex: 1, // Grow to fill available space
    }),
  },
});

export function FilterSelect<Option, IsMulti extends boolean = false>({
  variant = 'value',
  styles,
  ...props
}: SelectProps<Option, IsMulti, GroupBase<Option>> & {
  variant?: FilterSelectVariant;
}) {
  // Merge default styles with variant styles and any custom styles provided in props
  const mergedStyles = {
    ...getDefaultStyles(),
    ...getVariantStyles()[variant],
    ...(styles || {}),
  };

  return (
    <Select
      {...props}
      styles={mergedStyles}
      menuPlacement="auto"
      menuPosition="fixed" // Use fixed positioning for better handling in scrollable containers
    />
  );
}
